import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  isReservation,
  ServiceOccupant,
  updateReservationGuestCount,
  updateWalkInGuestCount,
} from 'restaurantAdmin/reservations/service/apiHelpers';
import { Button, ButtonVariants } from '@components/button/Button';
import { GuestCountPicker } from '@components/guestCountPicker/GuestCountPicker';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { Controller, useForm } from 'react-hook-form';
import styles from './UpdateGuestCountModal.scss';

export interface UpdateGuestCountModalProps {
  closeModal: () => void;
  isOpen: boolean;
  occupant: ServiceOccupant;
  onSuccess?: () => void;
}
export interface UpdateGuestFormData {
  guestCount: number;
}

export const UpdateGuestCountModal = ({
  closeModal,
  isOpen,
  occupant,
  onSuccess,
}: UpdateGuestCountModalProps) => {
  const { id: restaurantId, maxReservationGuests } = useRestaurant();

  const { control, handleSubmit } = useForm<UpdateGuestFormData>({
    defaultValues: {
      guestCount: occupant.guestCount,
    },
  });

  const handleOnSubmit = (data: UpdateGuestFormData) => {
    void (async () => {
      try {
        if (isReservation(occupant)) {
          await updateReservationGuestCount({
            guestCount: data.guestCount,
            reservationId: occupant.id,
            restaurantId,
          });
        } else {
          await updateWalkInGuestCount({
            guestCount: data.guestCount,
            walkInId: occupant.id,
            restaurantId,
          });
        }
        onSuccess?.();
      } catch (error) {
        if (error instanceof ApiError) {
          errorToast({
            message: `Failed to update guest count: ${error.message}`,
          });
        } else {
          errorToast({
            message: 'Failed to update guest count. Please try again.',
          });
        }

        reportAppError(error);
      }
    })();
    closeModal();
  };

  return (
    <Modal isOpen={isOpen} onClose={closeModal} title="Update Guest Count">
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <div className={styles.formInput}>
          <Controller
            control={control}
            name="guestCount"
            render={({ field: { onChange, value } }) => (
              <GuestCountPicker
                className={styles.guestCount}
                guestCount={value}
                max={maxReservationGuests}
                onChange={onChange}
              />
            )}
          />
        </div>
        <ModalActions>
          <Button
            label="Cancel"
            onClick={closeModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Save Count"
            type="submit"
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </form>
    </Modal>
  );
};
